import { useRef, useState, type PointerEvent } from "react";
import { Pipette } from "lucide-react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { ajustarParaLeitura } from "./production-stage-colors";

type Hsv = { h: number; s: number; v: number };

function hexParaHsv(hex: string): Hsv {
  const [r, g, b] = [0, 1, 2].map((i) => parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255);
  const max = Math.max(r, g, b);
  const d = max - Math.min(r, g, b);
  let h = 0;
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s: max ? d / max : 0, v: max };
}

function hsvParaHex({ h, s, v }: Hsv): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return Math.round((v - v * s * Math.max(0, Math.min(k, 4 - k, 1))) * 255);
  };
  return `#${[f(5), f(3), f(1)].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

const limitar = (n: number) => Math.min(1, Math.max(0, n));

/** Clicar e arrastar numa área: chama `aoMover` com a posição relativa (0–1) enquanto o botão está
 *  apertado — o ponteiro fica capturado, então dá pra sair da área sem soltar. */
function useArrastar(aoMover: (x: number, y: number) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const mover = (e: PointerEvent) => {
    const r = ref.current!.getBoundingClientRect();
    aoMover(limitar((e.clientX - r.left) / r.width), limitar((e.clientY - r.top) / r.height));
  };
  return {
    ref,
    onPointerDown: (e: PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      mover(e);
    },
    onPointerMove: (e: PointerEvent) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) mover(e);
    },
  };
}

/**
 * Qualquer cor, escolhida arrastando: quadrado de saturação × brilho e barra de matiz, com a prévia
 * já ajustada pra leitura. Só grava em "Usar esta cor" — cada clique gravando (como o seletor do
 * sistema fazia) não deixava procurar a cor.
 */
export function SeletorDeCorLivre({
  valor,
  disabled,
  previa,
  onEscolher,
}: {
  valor: string;
  disabled?: boolean;
  previa: string;
  onEscolher: (hex: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [hsv, setHsv] = useState<Hsv>(() => hexParaHsv(valor));
  const hex = ajustarParaLeitura(hsvParaHex(hsv));
  const quadrado = useArrastar((x, y) => setHsv((c) => ({ ...c, s: x, v: 1 - y })));
  const matiz = useArrastar((x) => setHsv((c) => ({ ...c, h: x * 359.9 })));

  return (
    <Popover
      open={aberto}
      onOpenChange={(v) => {
        setAberto(v);
        if (v) setHsv(hexParaHsv(valor));
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label="Escolher outra cor"
          className="flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-zinc-300 text-zinc-400 hover:border-zinc-400 hover:text-zinc-600 disabled:opacity-50 dark:border-border dark:text-muted-foreground"
        >
          <Pipette className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 space-y-3 border border-zinc-200 bg-white p-3 shadow-lg dark:border-border dark:bg-card">
        <div
          {...quadrado}
          role="slider"
          aria-label="Saturação e brilho"
          aria-valuenow={Math.round(hsv.s * 100)}
          className="relative h-36 w-full cursor-crosshair touch-none rounded-md"
          style={{
            backgroundColor: `hsl(${hsv.h} 100% 50%)`,
            backgroundImage: "linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)",
          }}
        >
          <span
            className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
          />
        </div>
        <div
          {...matiz}
          role="slider"
          aria-label="Matiz"
          aria-valuenow={Math.round(hsv.h)}
          className="relative h-3 w-full cursor-pointer touch-none rounded-full"
          style={{ backgroundImage: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}
        >
          <span
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="truncate rounded px-2 py-0.5 text-[11px] font-semibold" style={{ backgroundColor: `${hex}1a`, color: hex }}>
            {previa}
          </span>
          <span className="font-mono text-[11px] text-zinc-500">{hex.toUpperCase()}</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setAberto(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onEscolher(hex);
              setAberto(false);
            }}
          >
            Usar esta cor
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
